import { ChangeEvent } from 'react';

import {
  ChakraFlex,
  ChakraGridItem,
  ChakraLink,
  ChakraText,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  Menu,
  TextAreaInput,
} from '@equip.health/ui';

import {
  filterDisabledSubCategories,
  filterInActiveSubCategories,
  isDisabledSubCategory,
  parseDescriptionForUrlMarkup,
  taskPriorityDefinitions,
} from '~/lib/util/task.util';

import CategoryLabel from './CategoryLabel';
import PriorityLabel from './PriorityLabel';
import {
  CATEGORY_PATIENT_CARE,
  HG_LAB_RESULTS,
} from '~/lib/constants/task.constants';
const { neutral } = EquipUIFireflyV2Theme.colors;
const { label, labelBold, paragraph } = EquipUITheme.typography;

const CATEGORY_MARGIN = '16px 0 18px';

export type DetailsSectionProps = {
  categories: Category[];
  category: Category;
  description: string;
  isNonEditable: boolean;
  onChangeCategory: (value: string) => void;
  onChangeDescription: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onChangePriority: (value: string) => void;
  onChangeSubCategory: (value: string) => void;
  subcategory: string;
  taskPriorityId: string;
};

const DetailsSection = ({
  categories,
  category,
  description,
  isNonEditable,
  onChangeCategory,
  onChangeDescription,
  onChangePriority,
  onChangeSubCategory,
  subcategory,
  taskPriorityId,
}: DetailsSectionProps) => {
  const taskPriorityDefinition =
    taskPriorityDefinitions[Number.parseInt(taskPriorityId, 10) - 1];

  const hasDisabledSubCategory = isDisabledSubCategory(subcategory);
  const allowedSubCategories = filterInActiveSubCategories(
    filterDisabledSubCategories(category?.subCategoryList ?? []),
  );
  const shouldShowTaskDescriptionForLabResults =
    category?.categoryName === CATEGORY_PATIENT_CARE &&
    subcategory.toLowerCase() === HG_LAB_RESULTS;

  const isSubCategoryDisabled =
    shouldShowTaskDescriptionForLabResults || hasDisabledSubCategory;

  return (
    <ChakraGridItem
      borderTop={`2px solid ${neutral.line}`}
      padding="16px 0 30px"
    >
      <ChakraText {...labelBold} color="neutral.secondary" marginBottom="18px">
        Description
      </ChakraText>
      {isNonEditable || shouldShowTaskDescriptionForLabResults ? (
        <ChakraText
          {...paragraph}
          color="neutral.secondary"
          lineHeight="19.6px"
          padding="8px 14px 14px"
          whiteSpace="pre-line"
        >
          {description &&
            parseDescriptionForUrlMarkup(
              description,
              ChakraLink,
              shouldShowTaskDescriptionForLabResults,
            )}
        </ChakraText>
      ) : (
        <TextAreaInput
          backgroundColor="neutral.background.secondary"
          borderRadius="16px"
          color="neutral.secondary"
          fontSize="14px"
          id="Task Description"
          label={null}
          onChange={onChangeDescription}
          value={description ?? ''}
        />
      )}
      <ChakraFlex height="80px" marginLeft="14px">
        <ChakraFlex flexDirection="column" width="33%">
          {category && (
            <>
              <ChakraText {...label} margin={CATEGORY_MARGIN}>
                Category
              </ChakraText>
              {isNonEditable || shouldShowTaskDescriptionForLabResults ? (
                <CategoryLabel
                  categoryColor={category.categoryColor}
                  categoryName={category.categoryName}
                />
              ) : (
                <Menu
                  border="none"
                  buttonContent={
                    <CategoryLabel
                      categoryColor={category.categoryColor}
                      categoryName={category.categoryName}
                    />
                  }
                  fontSize="14px"
                  fontWeight="unset"
                  height="20px"
                  id="category"
                  onSelect={onChangeCategory}
                  options={(categories ?? []).map(
                    ({ categoryColor, categoryName }) => ({
                      value: categoryName,
                      node: (
                        <CategoryLabel
                          categoryColor={categoryColor}
                          categoryName={categoryName}
                        />
                      ),
                    }),
                  )}
                  outline="none"
                  padding="8px"
                />
              )}
            </>
          )}
        </ChakraFlex>
        {category?.subCategoryList?.length > 0 && (
          <ChakraFlex flexDirection="column" width="33%">
            <ChakraText {...label} margin={CATEGORY_MARGIN}>
              Sub-category
            </ChakraText>
            {isNonEditable || isSubCategoryDisabled ? (
              <ChakraText
                {...paragraph}
                height="40px"
                marginTop={!isNonEditable ? '6px' : undefined} // so text aligns in all cases
              >
                {subcategory}
              </ChakraText>
            ) : (
              allowedSubCategories.length > 0 && (
                <Menu
                  border="none"
                  buttonContent={
                    <ChakraText {...paragraph} color="neutral.primary">
                      {subcategory}
                    </ChakraText>
                  }
                  fontSize="14px"
                  fontWeight="unset"
                  height="20px"
                  id="subcategory"
                  listMaxHeight="300px"
                  onSelect={onChangeSubCategory}
                  options={allowedSubCategories.map(({ categoryName }) => ({
                    value: categoryName,
                    node: (
                      <ChakraText {...paragraph}>{categoryName}</ChakraText>
                    ),
                  }))}
                  outline="none"
                  overflow="scroll"
                  padding="8px"
                />
              )
            )}
          </ChakraFlex>
        )}
        <ChakraFlex flexDirection="column" width="33%">
          {taskPriorityDefinition && (
            <>
              <ChakraText {...label} margin={CATEGORY_MARGIN}>
                Priority
              </ChakraText>
              {isNonEditable ? (
                <PriorityLabel
                  color={taskPriorityDefinition.color}
                  title={taskPriorityDefinition.title}
                />
              ) : (
                <Menu
                  border="none"
                  buttonContent={
                    <PriorityLabel
                      color={taskPriorityDefinition.color}
                      title={taskPriorityDefinition.title}
                    />
                  }
                  fontSize="14px"
                  fontWeight="unset"
                  height="20px"
                  id="priority"
                  onSelect={onChangePriority}
                  options={taskPriorityDefinitions.map(
                    ({ title, value, color }) => ({
                      value,
                      node: <PriorityLabel color={color} title={title} />,
                    }),
                  )}
                  outline="none"
                  padding="8px"
                />
              )}
            </>
          )}
        </ChakraFlex>
      </ChakraFlex>
    </ChakraGridItem>
  );
};

export default DetailsSection;
